<template>
  <div class="view settings">
    <UserConsentPopup v-if="consentPopup.open" @updateUserConsent="handleUpdateUserConsent"/>
    <div class="top-bar">
      <div class="home-btn" :class="getSettingsCloseBtnState ? 'active' : ''">
        <img src="@/assets/icons/arrow.svg" alt="Home" @click="goBack">
      </div>
    </div>
    <div class="items-wrap">
        <Slider
          ref="slider"
          @scroll="onScroll"
        >
          <Setting
            v-for="item in settingList" :key="item.id"
            :title="item.title" :itemType="item.itemType"
            :options="item.options" ref="cards"
          />
        </Slider>
    </div>
  </div>
</template>

<script>
  import { soundList, soundPlayer } from '../utils/sounds';
  import { executeOnTV } from '../utils/utils';
  import langUtil, { StringKey } from '@/utils/lang.util';
  import getSettingOptionList from '@/utils/settings.options';
  import Slider from '../components/Slider.vue';
  import Setting from '@/components/Setting.vue';
  import UserConsentPopup from '@/components/UserConsentPopup.vue'
  import Vue from 'vue';

  export default {
    components: { Slider, Setting, UserConsentPopup },
    data() {
      return {
        isScrolling: false,
        settingList: getSettingOptionList,
        posFocus: 0,
        operaTv: false,
        newList: [],
        strings: {
          settings: langUtil.getLang().t(StringKey.Settings),
        },
        consentPopup: Vue.observable({
          open: false,
        }),
      };
    },
    created() {
      this.consentPopup.open = this.getToken && !this.getIsTcAccepted && !this.getIsEmailFake;
    },
    computed: {
      getSettingsCloseBtnState() {
        return this.$store.getters['settings/getSettingsCloseBtnState'];
      },
      getToken() {
        return this.$store.getters['account/getToken'];
      },
      getUserId() {
        return this.$store.getters['account/getUserId'];
      },
      getIsEmailFake() {
        return this.$store.getters['account/getIsEmailFake'];
      },
      getIsPromoAccepted() {
        return this.$store.getters['account/getIsPromoAccepted'];
      },
      getIsTcAccepted() {
        return this.$store.getters['account/getIsTcAccepted'];
      }
    },
    methods: {
      triggerScrollRefresh() {
        this.$refs.slider.refresh();
      },
      onTv() {
        this.operaTv = true;
      },
      notOnTv() {
        this.operaTv = false;
      },
      onScroll(scrollInfo) {
        this.$emit('scroll', scrollInfo);
        this.isScrolling = scrollInfo.isScrolling;
      },
      autoScrollIfNeeded(oldPosition, newPosition) {
        const slider = this.$refs.slider;
        const delta = oldPosition - newPosition;
        const size = 1200;
        const direction = delta === 0 ? null : delta > 0 ? 'reverse' : 'forward';
        if ((this.posFocus === 2 || this.posFocus === 3) && direction === 'forward') {
          slider.scrollHorizontally(size);
        } else if ((this.posFocus === 2 || this.posFocus === 1) && direction === 'reverse') {
          slider.scrollHorizontally(-size);
        }
      },
      handleKeyboard(event) {
        event.preventDefault();
        const oldPosition = this.posFocus;
        try {
          switch (event.keyCode) {
            case VK_LEFT: {
              if (this.posFocus > 0) {
                this.posFocus--;
              }
              break;
            }
            case VK_RIGHT: {
              if (this.posFocus < 4) {
                this.posFocus++;
              }
              break;
            }
          }
        } catch (error) {
          switch (event.code) {
            case 'ArrowLeft': {
              if (this.posFocus > 0) {
                this.posFocus--;
              }
              break;
            }
            case 'ArrowRight': {
              if (this.posFocus < 4) {
                this.posFocus++;
              }
              break;
            }
          }
          this.autoScrollIfNeeded(oldPosition, this.posFocus);
        }
        this.autoScrollIfNeeded(oldPosition, this.posFocus);
      },
      goBack() {
        soundPlayer(soundList.general);
        this.$router.go(-1);
      },
      handleUpdateUserConsent(data){
        if(this.getIsPromoAccepted !== data.promo || this.getIsTcAccepted !== data.termsCondition) {
          this.$store.dispatch('account/updateUserConsent', {
            'userId' : this.getUserId,
            'isPromoAccepted' : data.promo,
            'isTcAccepted' : data.termsCondition
          });
        }
        this.consentPopup.open = false;
      },
    },
    beforeMount() {
      executeOnTV(this.onTv, this.notOnTv);

      // hide the first card on tv
      if (this.operaTv && this.settingList.length > 5) {
        this.settingList.shift();
      }
    },
    mounted() {
      document.addEventListener('keyup', this.handleKeyboard);
    },
    destroyed() {
      document.removeEventListener('keyup', this.handleKeyboard);
    },
  };
</script>

<style scoped lang="scss">
  @import '../assets/styles/breakpoints.scss';

  @mixin transition($transition...) {
    -moz-transition: $transition;
    -o-transition: $transition;
    -webkit-transition: $transition;
    transition: $transition;
  }

  .settings {
    background-color:#29B6F6;
    background-image: url('~@/assets/backgrounds/bg_settings.svg');
    background-position: center;
    background-repeat: no-repeat;

    @media (max-width: $breakpoint-lg) {
      background-position: inherit;
    }

    @media (max-width: $breakpoint-md) {
      background-position: 25% 80%;
    }
  }

  .items-wrap {
    box-sizing: border-box;
    position: relative;

    @media (orientation: portrait) {
      height: 100%;

      .top-bar--has-height + & {
        height: calc(100% - 74px);
      }
    }

    @media (orientation: landscape) {
      height: 100%;
      padding: 20px 0;
    }
  }

  .top-bar {
    position: relative;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: space-between;

    @media (orientation: portrait) {
      &--has-height {
        box-sizing: border-box;
        height: 74px;
        padding: 10px 10px 0;
      }
    }

    img {
      vertical-align: middle;
    }

    .home-btn {
      position: absolute;
      top: 10px;
      left: 10px;
      cursor: pointer;
      outline: none;

      @media (min-width: $breakpoint-lg) {
        top: 20px;
        left: 20px;
      }

      &.active {
        transform: scale(1.1);
      }

      &:hover,
      &:focus {
        transform: scale(1.1);
      }

      img {
        width: 60px;
        height: 60px;

        @media (min-width: $breakpoint-lg) {
          width: 75px;
          height: 75px;
        }
        @media (max-width: 390px) {
          width: 50px;
          height: 50px;
        }
      }
    }
  }
</style>
